// FLAGS: consent
// Carries "this account came from a public event page" from the link the visitor clicked to the moment
// their account is identified.
//
// Flagged for consent because the *storage choice here is the enforcement*: session storage, cleared at
// identify, is what keeps this outside the territory that needs a consent basis. Moving it to
// `localStorage` to catch the visitor who returns tomorrow would turn it into a multi-day analytics
// identifier on a health domain — a Legal question, not a refactor. Deliberately not folded into UtmService: that one is named for, stored
// under and documented as the five `utm_*` campaign keys, and this is a different question with a
// different lifetime.
//
// Nothing here outlives the browser session, which is the whole scope of the attribution: the CTA opens
// a new tab carrying these parameters, the visitor registers in that tab, and `identifyUser` consumes
// them. Catching the visitor who returns the next day would mean persisting an analytics identifier for
// days, which needs a consent basis — see OI-25337.

/** The only source this accepts today. An unknown value is dropped rather than stored. */
const PUBLIC_EVENT_SOURCE = "public_event";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export interface PublicEventAttribution {
  src: string;
  event_id: string;
}

export class AttributionService {
  static readonly srcParam = "src";

  static readonly eventIdParam = "event_id";

  static readonly sessionStorageIdentifier = "publicEventAttribution";

  /**
   * Reads the pair off the landing URL and **deletes both from `searchParams`**, so the single
   * `UtmService.removeUtmParamsFromUrl` rewrite that follows in App.tsx drops them from the address
   * bar as well. Call this before that rewrite.
   *
   * Both values are validated rather than trusted: `posthog.service.ts` turns whatever it is handed
   * into a permanent person property, so an unvalidated pair would let any URL write arbitrary text
   * onto an identified person. Only the known source and a well-formed event id are stored, and a
   * half-pair is stored as nothing — an event id without a source is not attribution, and a source
   * without an event id cannot be joined to a customer.
   */
  static saveToSessionStorage(searchParams: URLSearchParams): void {
    const attribution = AttributionService.fromSearchParams(searchParams);

    searchParams.delete(AttributionService.srcParam);
    searchParams.delete(AttributionService.eventIdParam);

    if (!attribution) {
      return;
    }

    sessionStorage.setItem(
      AttributionService.sessionStorageIdentifier,
      JSON.stringify(attribution),
    );
  }

  /**
   * The pair as it still stands on a URL, validated by the same two rules and **without** touching
   * `searchParams`.
   *
   * Needed because App.tsx stores the pair in an effect, and a child's effects run before its
   * parent's: `useEmployerFromUrl` renders inside RegisterView and would read an empty session
   * storage on the first pass, with no re-render to correct it — the address-bar rewrite is a
   * `history.replaceState`, which react-router does not observe.
   */
  static fromSearchParams(
    searchParams: URLSearchParams,
  ): PublicEventAttribution | null {
    const src = searchParams.get(AttributionService.srcParam);
    const eventId = searchParams.get(AttributionService.eventIdParam);

    if (src !== PUBLIC_EVENT_SOURCE || !eventId || !UUID.test(eventId)) {
      return null;
    }

    return { src, event_id: eventId };
  }

  /** Null when the visitor did not arrive from a public event page, which is the common case. */
  static getFromSessionStorage(): PublicEventAttribution | null {
    const stored = sessionStorage.getItem(
      AttributionService.sessionStorageIdentifier,
    );
    if (!stored) {
      return null;
    }

    try {
      return JSON.parse(stored) as PublicEventAttribution;
    } catch {
      // Someone edited storage, or an old shape is left over. Attribution is not worth an exception
      // on a read path that runs during login.
      return null;
    }
  }

  static removeFromSessionStorage(): void {
    sessionStorage.removeItem(AttributionService.sessionStorageIdentifier);
  }
}
